import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import { getTasksRoutine, setTaskFiltersRoutine } from 'features/taskManagement/ducks/actions'
import { selectTasks, selectTasksLoading, selectTasksTotalPages, selectTasksCurrentPage } from 'features/taskManagement/ducks/selectors'
import { getTaskStats } from 'services/taskService'
import { taskStatusTint } from 'features/taskManagement/taskStatusConstants'
import { SkeletonRows } from 'components/Skeleton'
import { priorityMeta } from 'features/taskManagement/taskPriority'

const STATUS_LABEL = {
  unassigned: 'Unassigned',
  assigned: 'Assigned',
  pending_dependent: 'Pending',
  in_progress: 'In progress',
  on_hold: 'On hold',
  awaiting_approval: 'Pending approval',
  completed: 'Completed',
}

const STATUS_TONE = {
  unassigned: taskStatusTint('unassigned'),
  assigned: taskStatusTint('assigned'),
  pending_dependent: taskStatusTint('pending_dependent'),
  in_progress: taskStatusTint('in_progress'),
  on_hold: taskStatusTint('on_hold'),
  awaiting_approval: taskStatusTint('awaiting_approval'),
  completed: taskStatusTint('completed'),
}


const PortfolioTasksTab = ({ portfolioId, estateId }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const tasks = useSelector(selectTasks)
  const isLoading = useSelector(selectTasksLoading)
  const totalPages = useSelector(selectTasksTotalPages)
  const currentPage = useSelector(selectTasksCurrentPage)

  useEffect(() => {
    if (!portfolioId && !estateId) return
    dispatch(setTaskFiltersRoutine({ portfolioId, estateId, buildingId: null, search: '', status: null }))
    dispatch(getTasksRoutine({ page: 1 }))
  }, [dispatch, portfolioId, estateId])

  const [stats, setStats] = useState(null)
  useEffect(() => {
    if (!portfolioId && !estateId) { setStats(null); return }
    let cancelled = false
    getTaskStats(portfolioId ? { portfolioId } : { estateId })
      .then(res => { if (!cancelled) setStats(res?.data?.data || null) })
      .catch(() => { if (!cancelled) setStats(null) })
    return () => { cancelled = true }
  }, [portfolioId, estateId])

  const rows = useMemo(() => Array.isArray(tasks) ? tasks : [], [tasks])

  const overdueCount = useMemo(() => rows.filter(t => {
    if (!t.deadline) return false
    if (t.status === 'completed') return false
    return new Date(t.deadline) < new Date()
  }).length, [rows])

  const completedCount = useMemo(() => rows.filter(t => t.status === 'completed').length, [rows])

  const totalTasks = stats?.total ?? rows.length
  const openTotal = stats?.openTasks ?? rows.filter(t => t.status !== 'completed').length
  const overdueTotal = stats?.overdue ?? overdueCount
  const completedTotal = stats?.completed ?? completedCount

  const handleOpen = (task) => {
    navigate(`/tasks/${task.id}`)
  }

  const handleHubHandoff = () => {
    navigate(estateId ? `/tasks?estate=${estateId}&tab=tasks` : `/tasks?portfolio=${portfolioId}&tab=tasks`)
  }

  return (
    <Wrap>
      <ToolBar>
        <Stats>
          <Stat $tone="primary">
            <StatValue>{totalTasks}</StatValue>
            <StatLabel>Total</StatLabel>
          </Stat>
          <Stat $tone="open">
            <StatValue>{openTotal}</StatValue>
            <StatLabel>Open</StatLabel>
          </Stat>
          <Stat $tone="overdue">
            <StatValue>{overdueTotal}</StatValue>
            <StatLabel>Overdue</StatLabel>
          </Stat>
          <Stat $tone="done">
            <StatValue>{completedTotal}</StatValue>
            <StatLabel>Completed</StatLabel>
          </Stat>
        </Stats>
        <HubLink onClick={handleHubHandoff}>
          Open in Task Hub
          <LaunchOutlinedIcon style={{ fontSize: 14 }} />
        </HubLink>
      </ToolBar>

      {isLoading && rows.length === 0 && <SkeletonRows rows={5} />}

      {!isLoading && rows.length === 0 && (
        <EmptyState>
          <AssignmentTurnedInOutlinedIcon style={{ fontSize: 32, opacity: 0.6 }} />
          <div>No tasks across this {estateId ? 'estate' : 'portfolio'} yet.</div>
          <EmptyHint>Tasks appear here once they&apos;re created on any Building Passport with Task Management enabled.</EmptyHint>
        </EmptyState>
      )}

      {rows.length > 0 && (
        <List>
          <ListHeader>
            <Col $flex="2">Task</Col>
            <Col $flex="1.4">Building Passport</Col>
            <Col $flex="1">Status</Col>
            <Col $flex="0.8">Priority</Col>
            <Col $flex="1">Due</Col>
          </ListHeader>
          {rows.map(task => (
            <Row key={task.id} onClick={() => handleOpen(task)}>
              <Col $flex="2">
                <RowTitle>{task.title || 'Untitled task'}</RowTitle>
                {Array.isArray(task.assignees) && task.assignees.length > 0 && (
                  <RowMeta>{task.assignees.map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim()).filter(Boolean).join(', ')}</RowMeta>
                )}
              </Col>
              <Col $flex="1.4">
                <BpName>{task.building?.name || '—'}</BpName>
              </Col>
              <Col $flex="1">
                <StatusPill $tone={STATUS_TONE[task.status] || '#94a3b8'}>
                  {STATUS_LABEL[task.status] || task.status || '—'}
                </StatusPill>
              </Col>
              <Col $flex="0.8">
                {priorityMeta(task.priority) && <PriorityPill $tone={priorityMeta(task.priority).fg}>{priorityMeta(task.priority).label}</PriorityPill>}
              </Col>
              <Col $flex="1">
                {task.deadline ? formatDate(task.deadline) : <Muted>—</Muted>}
              </Col>
            </Row>
          ))}
          {currentPage < totalPages && (
            <LoadMoreRow
              onClick={() => dispatch(getTasksRoutine({ page: currentPage + 1 }))}
              disabled={isLoading}
            >
              {isLoading ? 'Loading…' : 'Load more tasks'}
            </LoadMoreRow>
          )}
        </List>
      )}
    </Wrap>
  )
}

const formatDate = (iso) => {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch (_) {
    return iso
  }
}

export default PortfolioTasksTab

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
`

const Stats = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

const TONE_BG = {
  primary: '#f8fafc',
  open: '#e6f7f9',
  overdue: '#fef2f2',
  done: '#f0fdf4',
}

const TONE_VALUE = {
  primary: '#0f172a',
  open: '#33becc',
  overdue: '#b91c1c',
  done: '#15803d',
}

const Stat = styled.div`
  min-width: 92px;
  background: ${({ $tone }) => TONE_BG[$tone] || '#f8fafc'};
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme, ...rest }) => TONE_VALUE[rest.$tone] || theme.colors.haiti};
`

const StatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 600;
`

const HubLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ theme }) => theme.colors.primary}14;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary}33;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}1f;
    border-color: ${({ theme }) => theme.colors.primary}55;
  }
`


const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  color: #475569;
  background: #fff;
  border: 1px dashed #e2e8f0;
  border-radius: 14px;
  gap: 8px;
  font-size: 14px;
`

const EmptyHint = styled.div`
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
`

const List = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
`

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 600;

  @media (max-width: 768px) { display: none; }
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #f8fafc; }
  &:last-child { border-bottom: none; }
`

const Col = styled.div`
  flex: ${({ $flex }) => $flex || 1};
  min-width: 0;
`

const RowTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowMeta = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BpName = styled.div`
  font-size: 13px;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StatusPill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $tone }) => $tone}22;
  color: ${({ $tone }) => $tone};
  text-transform: capitalize;
`

const PriorityPill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $tone }) => $tone}22;
  color: ${({ $tone }) => $tone};
  text-transform: capitalize;
`

const Muted = styled.span`
  color: #94a3b8;
`

const LoadMoreRow = styled.button`
  width: 100%;
  padding: 12px;
  background: #fff;
  border: none;
  border-top: 1px solid #f1f5f9;
  color: #33becc;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #f8fafc; }
  &:disabled { color: #94a3b8; cursor: default; }
`
